import type React from 'react'
import { createPortal } from 'react-dom'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { TooltipProvider } from '@/components/ui/tooltip'
import { cn } from '@/utils'
import {
  PHOTO_ACTION_DEFINITIONS,
  type PhotoActionDefinition,
  type PhotoActionId,
} from '@/lib/photos/photo-action-items'
import type { PhotoActionHandlers } from '@/components/app/photos/photo-actions-list'
import { stopPropagation } from '@/components/app/editor/tools/floating-toolbar'

import { PhotoActionButton } from './components/photo-action-button'
import { usePhotoMainToolbarPosition } from './hooks/use-photo-main-toolbar-position'

export interface PhotoMainToolbarProps {
  shape: ShapeData
  scale: number
  canvasRef: React.RefObject<HTMLDivElement | null>
  handlers: PhotoActionHandlers
  isDisabled: (id: PhotoActionId) => boolean
  isRemovingBackground?: boolean
  /** Actions whose trigger should show the pressed/active chrome (e.g. Photo effects open). */
  isActionActive?: (id: PhotoActionId) => boolean
  /** Which actions to show, in order. Defaults to every photo action; a placed ClikArt passes its
   *  own four (`CLIKART_PHOTO_ACTION_DEFINITIONS`). */
  actions?: PhotoActionDefinition[]
}

export function PhotoMainToolbar({
  shape,
  scale,
  canvasRef,
  handlers,
  isDisabled,
  isRemovingBackground = false,
  isActionActive,
  actions = PHOTO_ACTION_DEFINITIONS,
}: PhotoMainToolbarProps) {
  const { toolbarRef, pos } = usePhotoMainToolbarPosition(canvasRef, shape, scale)

  return createPortal(
    <TooltipProvider delayDuration={300}>
      <div
        ref={toolbarRef}
        className={cn(
          'fixed z-[60] flex h-12 w-max max-w-none flex-nowrap items-center gap-2 rounded-md border border-border bg-popover px-2 py-2 shadow-md',
          !pos && 'invisible pointer-events-none'
        )}
        style={pos ? { left: pos.x, top: pos.y } : { left: 0, top: 0 }}
        data-photo-main-toolbar
        onPointerDown={stopPropagation}
        onPointerUp={stopPropagation}
        onClick={stopPropagation}
      >
        {actions.map((action) => (
          <PhotoActionButton
            key={action.id}
            action={action}
            handlers={handlers}
            isDisabled={isDisabled}
            isRemovingBackground={isRemovingBackground}
            isActionActive={isActionActive}
          />
        ))}
      </div>
    </TooltipProvider>,
    document.body
  )
}
